import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useTCoins } from '../../context/TCoinsContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { hasRole, ROLES, isMarketplaceUser } from '../../utils/roleManager';

const BottomNavigation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { getTotalTCoins } = useTCoins();
  const { cart } = useMarketplace();
  const [showQuickMenu, setShowQuickMenu] = useState(false);

  const isActive = (path) => {
    if (path === '/pharma-quiz' || path === '/mothai-quiz') {
      return location.pathname === '/pharma-quiz' || location.pathname === '/mothai-quiz';
    }
    if (path === '/marketplace' || path === '/') {
      return location.pathname === path || location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // กำหนด Bottom Navigation Items ตาม Role
  const getNavItems = () => {
    if (!currentUser) {
      // ไม่ได้ login
      return [
        {
          path: '/pharma-quiz',
          label: 'ติวข้อสอบ',
          icon: '📚',
          show: true
        },
        {
          path: '/login',
          label: t('bottomNav.login'),
          icon: '🔐',
          show: true
        },
        {
          path: '/profile',
          label: t('bottomNav.profile'),
          icon: '👤',
          show: true
        }
      ];
    }

    // Marketplace Users (USER, FARM)
    if (isMarketplaceUser(currentUser)) {
      return [
        {
          path: '/pharma-quiz',
          label: 'ติวข้อสอบ',
          icon: '📚',
          show: true
        },
        {
          path: '/marketplace/cart',
          label: t('bottomNav.cart'),
          icon: '🛒',
          show: true,
          badge: cart?.length || 0
        },
        {
          path: '/marketplace/orders',
          label: t('bottomNav.order'),
          icon: '📦',
          show: true
        },
        {
          path: '/profile',
          label: t('bottomNav.me'),
          icon: '👤',
          show: true
        }
      ];
    }

    // Customer (Booking System)
    if (hasRole(currentUser, ROLES.CUSTOMER)) {
      return [
        {
          path: '/pharma-quiz',
          label: 'ติวข้อสอบ',
          icon: '📚',
          show: true
        },
        {
          path: '/bookings',
          label: t('bottomNav.booking'),
          icon: '📋',
          show: true
        },
        {
          path: '/profile',
          label: t('bottomNav.me'),
          icon: '👤',
          show: true
        }
      ];
    }

    // Momo (Manager)
    if (hasRole(currentUser, ROLES.MOMO)) {
      return [
        {
          path: '/pharma-quiz',
          label: 'ติวข้อสอบ',
          icon: '📚',
          show: true
        },
        {
          path: '/momo',
          label: t('bottomNav.dashboard'),
          icon: '👨‍💼',
          show: true
        },
        {
          path: '/profile',
          label: t('bottomNav.me'),
          icon: '👤',
          show: true
        }
      ];
    }

    // Girl
    if (hasRole(currentUser, ROLES.GIRL)) {
      return [
        {
          path: '/pharma-quiz',
          label: 'ติวข้อสอบ',
          icon: '📚',
          show: true
        },
        {
          path: '/girl',
          label: t('bottomNav.schedule'),
          icon: '📅',
          show: true
        },
        {
          path: '/profile',
          label: t('bottomNav.me'),
          icon: '👤',
          show: true
        }
      ];
    }

    // Default (Admin or others)
    return [
      {
        path: '/pharma-quiz',
        label: 'ติวข้อสอบ',
        icon: '📚',
        show: true
      },
      {
        path: '/profile',
        label: t('bottomNav.me'),
        icon: '👤',
        show: true
      }
    ];
  };

  const navItems = getNavItems().filter(item => item.show);

  const [quickMenuType, setQuickMenuType] = useState(null);

  const handleQuickMenuClick = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    // Toggle menu: ถ้าเปิดอยู่ให้ปิด, ถ้าปิดอยู่ให้เปิด
    if (showQuickMenu) {
      setShowQuickMenu(false);
      setQuickMenuType(null);
    } else {
      setShowQuickMenu(true);
      // Reset quickMenuType เมื่อเปิด menu ใหม่
      setQuickMenuType(null);
    }
  };

  const handleBackToMain = () => {
    setQuickMenuType(null);
    // ไม่ปิด menu เมื่อกลับไปหน้า main
  };

  // Marketplace Quick Menu Handlers
  const categories = [
    { id: 'Flowers', name: 'Flowers', icon: '🌸' },
    { id: 'Seeds', name: 'Seeds', icon: '🌱' },
    { id: 'Edibles', name: 'Edibles', icon: '🍪' },
    { id: 'Pre Rolls', name: 'Pre Rolls', icon: '🚬' },
    { id: 'Cannabis Oils', name: 'Oils', icon: '💧' },
    { id: 'CBD Products', name: 'CBD', icon: '💊' },
  ];

  const handleCategoryClick = (categoryId) => {
    navigate(`/marketplace?category=${categoryId}`);
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleAddProduct = () => {
    navigate('/marketplace/my-store?tab=products&add=true');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleCreateAuction = () => {
    navigate('/marketplace/auctions');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleBuyerRequest = () => {
    navigate('/marketplace/buyer-requests');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  // Booking System Handlers
  const handleCreateBooking = () => {
    navigate('/trip-plans');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleViewProfiles = () => {
    navigate('/trip-plans');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleTripPlans = () => {
    navigate('/trip-plans');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  // Momo Handlers
  const handleAddGirl = () => {
    navigate('/momo?action=addGirl');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleManageSchedule = () => {
    navigate('/momo?action=schedule');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleViewReports = () => {
    navigate('/momo?action=reports');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  // Girl Handlers
  const handleUpdateSchedule = () => {
    navigate('/girl?action=schedule');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleViewRevenue = () => {
    navigate('/girl?action=revenue');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  const handleViewBookings = () => {
    navigate('/girl?action=bookings');
    setShowQuickMenu(false);
    setQuickMenuType(null);
  };

  // Render Quick Menu based on Role
  const renderQuickMenu = () => {
    if (!currentUser) {
      return (
        <div className="bg-white rounded-lg shadow-xl p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{t('quickMenu.title')}</h3>
          <p className="text-center text-gray-600 mb-4">{t('quickMenu.pleaseLogin')}</p>
          <button
            onClick={() => {
              navigate('/login');
              setShowQuickMenu(false);
            }}
            className="w-full p-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
          >
            {t('common.login')}
          </button>
        </div>
      );
    }

    // Marketplace Users (USER, FARM)
    if (isMarketplaceUser(currentUser)) {
      if (!quickMenuType) {
        return (
          <div className="bg-white rounded-lg shadow-xl p-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{t('quickMenu.title')}</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleAddProduct();
                }}
                className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
              >
                <span className="text-3xl mb-2">🏪</span>
                <span className="text-sm font-medium text-gray-700">{t('quickMenu.sell')}</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setQuickMenuType('buy');
                }}
                className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
              >
                <span className="text-3xl mb-2">🛒</span>
                <span className="text-sm font-medium text-gray-700">{t('quickMenu.buy')}</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateAuction();
                }}
                className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
              >
                <span className="text-3xl mb-2">🔨</span>
                <span className="text-sm font-medium text-gray-700">{t('quickMenu.auction')}</span>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleBuyerRequest();
                }}
                className="flex flex-col items-center justify-center p-4 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
              >
                <span className="text-3xl mb-2">📢</span>
                <span className="text-sm font-medium text-gray-700">{t('quickMenu.buyerRequest')}</span>
              </button>
            </div>
          </div>
        );
      } else if (quickMenuType === 'buy') {
        return (
          <div className="bg-white rounded-lg shadow-xl p-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleBackToMain();
              }}
              className="mb-4 text-gray-600 hover:text-gray-800 flex items-center gap-2"
            >
              ← {t('quickMenu.back')}
            </button>
            <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{t('quickMenu.buy')}</h3>
            
            {/* Category Shortcuts */}
            <div className="mb-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">{t('quickMenu.productCategories')}</p>
              <div className="grid grid-cols-3 gap-2">
                {categories.slice(0, 6).map(category => (
                  <button
                    key={category.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCategoryClick(category.id);
                    }}
                    className="flex flex-col items-center justify-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-2xl mb-1">{category.icon}</span>
                    <span className="text-xs font-medium text-gray-700">{category.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Buyer Request Button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleBuyerRequest();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">📢</span>
              <span>{t('quickMenu.buyerRequest')}</span>
            </button>
          </div>
        );
      }
    }

    // Customer (Booking System)
    if (hasRole(currentUser, ROLES.CUSTOMER)) {
      return (
        <div className="bg-white rounded-lg shadow-xl p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{t('quickMenu.title')}</h3>
          <div className="space-y-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCreateBooking();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">📅</span>
              <span>{t('quickMenu.createBooking')}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewProfiles();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">👥</span>
              <span>{t('quickMenu.viewProfiles')}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleTripPlans();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">🗺️</span>
              <span>{t('quickMenu.tripPlans')}</span>
            </button>
          </div>
        </div>
      );
    }

    // Momo (Manager)
    if (hasRole(currentUser, ROLES.MOMO)) {
      return (
        <div className="bg-white rounded-lg shadow-xl p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{t('quickMenu.title')}</h3>
          <div className="space-y-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleAddGirl();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">👩</span>
              <span>{t('quickMenu.addGirl')}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleManageSchedule();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">📅</span>
              <span>{t('quickMenu.manageSchedule')}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewReports();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">📊</span>
              <span>{t('quickMenu.viewReports')}</span>
            </button>
          </div>
        </div>
      );
    }

    // Girl
    if (hasRole(currentUser, ROLES.GIRL)) {
      return (
        <div className="bg-white rounded-lg shadow-xl p-4">
          <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{t('quickMenu.title')}</h3>
          <div className="space-y-3">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleUpdateSchedule();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">📅</span>
              <span>{t('quickMenu.updateSchedule')}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewRevenue();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">💰</span>
              <span>{t('quickMenu.viewRevenue')}</span>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewBookings();
              }}
              className="w-full flex items-center justify-center gap-3 p-4 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition-colors"
            >
              <span className="text-2xl">📋</span>
              <span>{t('quickMenu.viewBookings')}</span>
            </button>
          </div>
        </div>
      );
    }

    // Default (Admin or others)
    return (
      <div className="bg-white rounded-lg shadow-xl p-4">
        <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">{t('quickMenu.title')}</h3>
        <p className="text-center text-gray-600">{t('quickMenu.noActions')}</p>
      </div>
    );
  };

  return (
    <>
      {/* Quick Menu Modal */}
      {showQuickMenu && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden"
          onClick={() => {
            setShowQuickMenu(false);
            setQuickMenuType(null);
          }}
        >
          <div className="absolute bottom-20 left-0 right-0 p-4">
            <div onClick={(e) => e.stopPropagation()}>
              {renderQuickMenu()}
            </div>
          </div>
        </div>
      )}

      <nav 
        className="fixed bottom-0 left-0 right-0 border-t z-50 md:hidden shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #1A3C34 0%, #2D5A4A 100%)',
          borderColor: '#D4AF37'
        }}
      >
        <div className="flex justify-around items-center h-16 px-2">
          {/* แบ่ง navItems เป็น 2 ส่วน: ทางซ้ายและทางขวาของปุ่ม + */}
          {(() => {
            const midPoint = Math.ceil(navItems.length / 2);
            const leftItems = navItems.slice(0, midPoint);
            const rightItems = navItems.slice(midPoint);
            
            return (
              <>
                {/* Items ทางซ้าย */}
                {leftItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                        active
                          ? 'text-white'
                          : 'text-gray-300'
                      }`}
                      style={active ? {
                        color: '#D4AF37',
                        fontWeight: 'bold'
                      } : {}}
                    >
                      <div className="relative mb-1">
                        <span className={`text-2xl ${active ? 'scale-110' : ''} transition-transform`}>
                          {item.icon}
                        </span>
                        {item.badge > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-medium ${active ? 'font-semibold' : ''}`}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
                
                {/* + Button in the middle - ไม่มี label */}
                <button
                  onClick={handleQuickMenuClick}
                  className="flex flex-col items-center justify-center flex-1 h-full transition-colors relative"
                >
                  <div className="relative mb-1">
                    <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                    style={{
                      background: 'linear-gradient(135deg, #D4AF37 0%, #996515 100%)'
                    }}
                  >
                      <span className="text-2xl text-white font-bold">+</span>
                    </div>
                  </div>
                  {/* ไม่แสดง label */}
                </button>

                {/* Items ทางขวา */}
                {rightItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                        active
                          ? 'text-white'
                          : 'text-gray-300'
                      }`}
                      style={active ? {
                        color: '#D4AF37',
                        fontWeight: 'bold'
                      } : {}}
                    >
                      <div className="relative mb-1">
                        <span className={`text-2xl ${active ? 'scale-110' : ''} transition-transform`}>
                          {item.icon}
                        </span>
                        {item.badge > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-medium ${active ? 'font-semibold' : ''}`}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </>
            );
          })()}
        </div>
      </nav>
    </>
  );
};

export default BottomNavigation;
